/**
 * Session 53 — demo autopilot. When `state.demoScript` is non-empty
 * (set ONLY by createDemoSession on the backend), this module fires
 * a delayed action per phase so the demo runs end-to-end without the
 * organizer typing anything. Result: a hands-off "watch mode" of the
 * full quest from riddle → geo verify → site-code → next location.
 *
 * Safety: the bot reads from `state.demoScript`, which is plaintext
 * answers loaded server-side. Real player joins NEVER carry this field
 * (org-token gated in createDemoSession). If demoScript is empty, the
 * bot is a no-op.
 *
 * Timing (user-tuned 2026-04-19): the bot fires when the stage timer
 * has FIRE_AT_REMAINING_SEC (5 in-game minutes) left, NOT after a fixed
 * blanket delay. This lets the demo show a realistic countdown — the
 * timer ticks down, then the bot submits "with seconds to spare".
 * The demoSpeedMultiplier collapses real-time (5 in-game min @ 50× ≈
 * 6 real sec). See `computeBotDelayMs` for the full math.
 *
 * A single pending setTimeout. `scheduleDemoBotAction` cancels the prior
 * pending fire before queueing a new one, so re-rendering a phase (or
 * transitioning) cannot stack multiple bot fires.
 */

import { getState, type DemoStep } from '../store';
import { getElapsedSec } from './time-utils';

export type DemoPhase = 'riddle' | 'input' | 'site-code';

/** Fire when this many in-game seconds remain on the location timer.
 *  User-requested 2026-04-19: a 3.5s blanket delay made the demo blow
 *  past the timer too fast — they wanted to see the countdown actually
 *  count down. Picking a fixed in-game offset means the bot fires near
 *  the end regardless of stage timeLimit, while the demoSpeedMultiplier
 *  collapses the wall-clock wait (5 in-game min @ 50× = 6 real sec). */
const FIRE_AT_REMAINING_SEC = 5 * 60;

/** Hard floor on real-time delay so the screen has time to render and a
 *  human can see the bot do something. Stages where elapsed already
 *  exceeds the fire-at threshold (e.g. mid-phase re-render) still wait
 *  this long before clicking. */
const MIN_REAL_DELAY_MS = 800;

/** Hard cap so an absurdly long timeLimit (24h "open-ended" quest) doesn't
 *  freeze the demo in the riddle phase for an hour. */
const MAX_REAL_DELAY_MS = 90_000;

let pending: ReturnType<typeof setTimeout> | null = null;

/** Cancel any pending bot fire. Safe to call any time. */
export function cancelDemoBot(): void {
  if (pending) {
    clearTimeout(pending);
    pending = null;
  }
}

/** Returns the script step matching the team's current location index,
 *  or null if no demo script is loaded / index is past the end. */
export function getDemoStep(): DemoStep | null {
  const s = getState();
  if (!s.demoScript || s.demoScript.length === 0) return null;
  const step = s.demoScript[s.currentLocationIndex];
  return step ?? null;
}

/** Compute when the bot should fire for the current location, expressed
 *  as a real-time delay in milliseconds. Rule:
 *
 *    1. Compute in-game seconds left on the stage timer.
 *    2. Bot fires when `FIRE_AT_REMAINING_SEC` (5 in-game minutes) remain.
 *    3. Translate that wait to real time via `demoSpeedMultiplier`
 *       (50× → 5 in-game min == 6 real sec).
 *    4. Floor at MIN_REAL_DELAY_MS (visibility) and cap at
 *       MAX_REAL_DELAY_MS (don't freeze on absurd timeLimits).
 *
 *  Exported for tests + so callers can override per-phase if they want.
 */
export function computeBotDelayMs(): number {
  const s = getState();
  const loc = s.locations[s.currentLocationIndex];
  if (!loc || !loc.timeLimitSec) return MIN_REAL_DELAY_MS;
  const elapsedInGameSec = getElapsedSec(s.locationStartedAt);
  const remainingInGameSec = loc.timeLimitSec - elapsedInGameSec;
  const inGameWaitSec = remainingInGameSec - FIRE_AT_REMAINING_SEC;
  if (inGameWaitSec <= 0) return MIN_REAL_DELAY_MS;
  const multiplier = s.demoSpeedMultiplier || 1;
  const realMs = (inGameWaitSec / multiplier) * 1000;
  return Math.max(MIN_REAL_DELAY_MS, Math.min(MAX_REAL_DELAY_MS, Math.round(realMs)));
}

/** Schedule the bot to perform the right action for `phase`. By default
 *  the delay is computed from the current timer state via
 *  `computeBotDelayMs` so the bot fires "5 in-game minutes before the
 *  stage runs out". Pass an explicit `delayMs` only for tests.
 *
 *  Idempotent: cancels the prior pending fire before queueing a new one,
 *  so re-rendering a phase or transitioning between phases cannot stack
 *  timers. No-op when `state.demoScript` is empty (real-player joins).
 */
export function scheduleDemoBotAction(
  phase: DemoPhase,
  container: HTMLElement,
  delayMs?: number,
): void {
  cancelDemoBot();
  const step = getDemoStep();
  if (!step) return;
  const wait = delayMs ?? computeBotDelayMs();
  pending = setTimeout(() => {
    pending = null;
    runBotAction(phase, container, step);
  }, wait);
}

function runBotAction(phase: DemoPhase, container: HTMLElement, step: DemoStep): void {
  if (phase === 'riddle') {
    container.querySelector<HTMLButtonElement>('#loc-answer-btn')?.click();
    return;
  }

  if (phase === 'input') {
    if (step.answerType === 'COORDINATES' && step.lat != null && step.lon != null) {
      const lat = container.querySelector<HTMLInputElement>('#geo-lat');
      const lon = container.querySelector<HTMLInputElement>('#geo-lon');
      if (lat) lat.value = String(step.lat);
      if (lon) lon.value = String(step.lon);
    } else if (step.answerType === 'ADDRESS') {
      // The verify endpoint accepts free-text city/street/house fallback
      // when objectIds aren't picked from FIAS autocomplete. We don't
      // dispatch an `input` event because that would kick the FIAS XHR
      // and race the subsequent submit click. Plain-text path is enough
      // for the online demo. Offline-mode demo + ADDRESS would require
      // FIAS suggestion clicks; not in scope.
      const city = container.querySelector<HTMLInputElement>('#geo-city');
      const street = container.querySelector<HTMLInputElement>('#geo-street');
      const house = container.querySelector<HTMLInputElement>('#geo-house');
      if (city && step.city) city.value = step.city;
      if (street && step.street) street.value = step.street;
      if (house && step.house) house.value = step.house;
    }
    container.querySelector<HTMLButtonElement>('#geo-submit')?.click();
    return;
  }

  if (phase === 'site-code') {
    // Empty siteCode → no required codes for this stop; click skip if
    // present (all-optional), else submit (server tolerates blank when
    // requiredFlags are all false).
    if (!step.siteCode) {
      const skip = container.querySelector<HTMLButtonElement>('#site-skip');
      (skip ?? container.querySelector<HTMLButtonElement>('#site-submit'))?.click();
      return;
    }
    const codes = step.siteCode.split('|');
    container.querySelectorAll<HTMLInputElement>('.site-code-field').forEach((input, idx) => {
      input.value = codes[idx] ?? '';
      // Trigger 'input' so the submit-button enablement listener (if any)
      // re-evaluates required-flag fulfilment before we click.
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
    container.querySelector<HTMLButtonElement>('#site-submit')?.click();
    return;
  }
}
